import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, TextField, Grid,
  Chip, Card, CardContent, CardActions, IconButton, Tooltip,
  Alert, CircularProgress, Divider, Stack, alpha, Select,
  MenuItem, FormControl, InputLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, LinearProgress,
} from '@mui/material';
import {
  Code as CodeIcon,
  Share as ShareIcon,
  CloudUpload as CloudUploadIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Upload as UploadIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const HUB_API = `${API_BASE}/strategies/api/pinescript-hub/`;
const UPLOAD_API = `${API_BASE}/strategies/api/pinescript-hub/upload-trades/`;

const TIMEFRAMES = ['30s', '45s', '1m', '5m', '15m', '1h', '4h', '1d'];
const ASSETS = ['NQ', 'QQQ', 'GC', 'CL', 'ZN'];
const TRADING_HOURS = ['ETH', 'RTH'];

const dark = {
  bg: '#0a0e14',
  card: '#111827',
  border: alpha('#00d4ff', 0.15),
  text: '#e2e8f0',
  muted: alpha('#e2e8f0', 0.5),
  accent: '#00d4ff',
  green: '#00ff88',
  purple: '#764ba2',
  amber: '#f59e0b',
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Box sx={{ position: 'relative', bgcolor: '#0d1117', borderRadius: 1, border: `1px solid ${dark.border}` }}>
      <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ position: 'absolute', top: 8, right: 8, color: copied ? dark.green : dark.muted, zIndex: 1 }}
        >
          {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Box
        component="pre"
        sx={{
          m: 0, p: 2, pr: 5, overflow: 'auto', maxHeight: 400,
          fontSize: '0.75rem', lineHeight: 1.6, color: '#a9b1d6',
          fontFamily: '"Fira Code", "JetBrains Mono", monospace',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}
      >
        {code}
      </Box>
    </Box>
  );
}

function parseMappings(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

function StrategyCard({ strategy, onView }) {
  const mappings = parseMappings(strategy.signal_mappings);
  return (
    <Card sx={{ bgcolor: dark.card, border: `1px solid ${dark.border}`, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1" fontWeight={700} color={dark.text} sx={{ lineHeight: 1.3 }}>
            {strategy.name}
          </Typography>
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            <Chip label={strategy.timeframe} size="small" sx={{ bgcolor: alpha(dark.accent, 0.15), color: dark.accent, fontWeight: 600, fontSize: '0.65rem' }} />
            <Chip label={strategy.asset} size="small" sx={{ bgcolor: alpha(dark.green, 0.15), color: dark.green, fontWeight: 600, fontSize: '0.65rem' }} />
            <Chip label={strategy.trading_hour} size="small" sx={{ bgcolor: alpha(dark.amber, 0.15), color: dark.amber, fontWeight: 600, fontSize: '0.65rem' }} />
          </Stack>
        </Stack>
        {strategy.description && (
          <Typography variant="body2" color={dark.muted} mb={1.5} sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {strategy.description}
          </Typography>
        )}
        {strategy.author_name && (
          <Typography variant="caption" color={dark.muted}>by {strategy.author_name}</Typography>
        )}
        <Box mt={1.5}>
          <Typography variant="caption" color={dark.muted} display="block" mb={0.5}>Signal Mappings</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
            {Object.entries(mappings).map(([k, v]) => (
              <Chip key={k} label={`${k} → ${v}`} size="small"
                sx={{ bgcolor: alpha(dark.purple, 0.2), color: '#c084fc', fontSize: '0.6rem', fontFamily: 'monospace' }} />
            ))}
          </Stack>
        </Box>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" startIcon={<VisibilityIcon />} onClick={() => onView(strategy)}
          sx={{ color: dark.accent, textTransform: 'none', fontWeight: 600 }}>
          View Code
        </Button>
        <Typography variant="caption" color={dark.muted} sx={{ ml: 'auto' }}>
          {strategy.created_at ? new Date(strategy.created_at).toLocaleDateString() : ''}
        </Typography>
      </CardActions>
    </Card>
  );
}

// ── Browse Tab ────────────────────────────────────────────────────────────────
function BrowseTab({ strategies, loading }) {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = strategies.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.description || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.author_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <TextField
          placeholder="Search strategies…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{
            width: 320,
            '& .MuiOutlinedInput-root': { bgcolor: dark.card, color: dark.text,
              '& fieldset': { borderColor: dark.border },
              '&:hover fieldset': { borderColor: dark.accent },
            },
            '& input::placeholder': { color: dark.muted },
          }}
        />
        <Typography variant="body2" color={dark.muted}>
          {filtered.length} {filtered.length === 1 ? 'strategy' : 'strategies'}
        </Typography>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2, bgcolor: alpha(dark.accent, 0.1), '& .MuiLinearProgress-bar': { bgcolor: dark.accent } }} />}

      {!loading && filtered.length === 0 && (
        <Box textAlign="center" py={6}>
          <CodeIcon sx={{ fontSize: 48, color: dark.muted, mb: 1 }} />
          <Typography color={dark.muted}>No strategies found. Be the first to share one!</Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        {filtered.map(s => (
          <Grid item xs={12} md={6} lg={4} key={s.id}>
            <StrategyCard strategy={s} onView={setSelected} />
          </Grid>
        ))}
      </Grid>

      {/* Code Viewer Dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: dark.card, border: `1px solid ${dark.border}` } }}>
        {selected && (
          <>
            <DialogTitle sx={{ color: dark.text, borderBottom: `1px solid ${dark.border}`, pb: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CodeIcon sx={{ color: dark.accent }} />
                <Typography fontWeight={700}>{selected.name}</Typography>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              {selected.description && (
                <Typography color={dark.muted} mb={2}>{selected.description}</Typography>
              )}
              <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={1}>
                <Chip label={`Timeframe: ${selected.timeframe}`} size="small" sx={{ bgcolor: alpha(dark.accent, 0.15), color: dark.accent }} />
                <Chip label={`Asset: ${selected.asset}`} size="small" sx={{ bgcolor: alpha(dark.green, 0.15), color: dark.green }} />
                <Chip label={`Hours: ${selected.trading_hour}`} size="small" sx={{ bgcolor: alpha(dark.amber, 0.15), color: dark.amber }} />
              </Stack>
              <Typography variant="caption" color={dark.muted} display="block" mb={1}>PineScript Code</Typography>
              <CodeBlock code={selected.code} />
              {Object.keys(parseMappings(selected.signal_mappings)).length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" color={dark.muted} display="block" mb={1}>Signal Mappings (for CSV upload)</Typography>
                  <Box sx={{ bgcolor: '#0d1117', borderRadius: 1, border: `1px solid ${dark.border}`, p: 1.5 }}>
                    <Typography component="pre" sx={{ m: 0, fontSize: '0.75rem', color: '#a9b1d6', fontFamily: 'monospace' }}>
                      {JSON.stringify(parseMappings(selected.signal_mappings), null, 2)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ borderTop: `1px solid ${dark.border}`, px: 2 }}>
              <Button onClick={() => setSelected(null)} sx={{ color: dark.muted }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

// ── Share Tab ─────────────────────────────────────────────────────────────────
function ShareTab({ onPublished }) {
  const [form, setForm] = useState({
    name: '', description: '', code: '', author_name: '',
    timeframe: '1m', asset: 'NQ', trading_hour: 'ETH',
    signal_mappings: '{\n  "MW Long": "SMART_BAND_15",\n  "MW Short": "SMART_BAND_15_SHORT"\n}',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    let signal_mappings;
    try {
      signal_mappings = JSON.parse(form.signal_mappings);
    } catch {
      setError('Signal Mappings is not valid JSON');
      return;
    }
    if (!form.name.trim()) { setError('Strategy name is required'); return; }
    if (!form.code.trim()) { setError('PineScript code is required'); return; }

    setLoading(true);
    try {
      const resp = await fetch(HUB_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, signal_mappings }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to publish');
      setSuccess(`Strategy published (ID: ${data.id})`);
      setForm(f => ({ ...f, name: '', description: '', code: '', author_name: '' }));
      onPublished();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: dark.card, color: dark.text,
      '& fieldset': { borderColor: dark.border },
      '&:hover fieldset': { borderColor: dark.accent },
      '&.Mui-focused fieldset': { borderColor: dark.accent },
    },
    '& .MuiInputLabel-root': { color: dark.muted },
    '& .MuiInputLabel-root.Mui-focused': { color: dark.accent },
    '& textarea': { color: dark.text },
  };

  const selectSx = {
    bgcolor: dark.card, color: dark.text,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: dark.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dark.accent },
    '& .MuiSvgIcon-root': { color: dark.muted },
  };

  return (
    <Box maxWidth={800}>
      <Typography variant="h6" color={dark.text} fontWeight={700} mb={0.5}>
        Share a PineScript Strategy
      </Typography>
      <Typography variant="body2" color={dark.muted} mb={3}>
        Publish your PineScript strategy so other TradeWave users can use it in TradingView.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Stack spacing={2.5}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField fullWidth label="Strategy Name" value={form.name} onChange={set('name')} sx={fieldSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Your Name / Team" value={form.author_name} onChange={set('author_name')} sx={fieldSx} />
          </Grid>
        </Grid>

        <TextField fullWidth label="Description" value={form.description} onChange={set('description')}
          multiline rows={2} sx={fieldSx} />

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: dark.muted }}>Timeframe</InputLabel>
              <Select value={form.timeframe} onChange={set('timeframe')} label="Timeframe" sx={selectSx}
                MenuProps={{ PaperProps: { sx: { bgcolor: dark.card, color: dark.text } } }}>
                {TIMEFRAMES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: dark.muted }}>Asset</InputLabel>
              <Select value={form.asset} onChange={set('asset')} label="Asset" sx={selectSx}
                MenuProps={{ PaperProps: { sx: { bgcolor: dark.card, color: dark.text } } }}>
                {ASSETS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: dark.muted }}>Trading Hours</InputLabel>
              <Select value={form.trading_hour} onChange={set('trading_hour')} label="Trading Hours" sx={selectSx}
                MenuProps={{ PaperProps: { sx: { bgcolor: dark.card, color: dark.text } } }}>
                {TRADING_HOURS.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box>
          <Typography variant="caption" color={dark.muted} display="block" mb={0.5}>
            Signal Mappings (JSON) — maps TradingView signal names → TradeWave signal types
          </Typography>
          <TextField fullWidth multiline rows={5} value={form.signal_mappings}
            onChange={set('signal_mappings')}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            sx={fieldSx} />
        </Box>

        <Box>
          <Typography variant="caption" color={dark.muted} display="block" mb={0.5}>
            PineScript Code
          </Typography>
          <TextField fullWidth multiline rows={16} value={form.code} onChange={set('code')}
            placeholder="//@version=5&#10;strategy(...)"
            inputProps={{ style: { fontFamily: '"Fira Code", monospace', fontSize: '0.75rem' } }}
            sx={fieldSx} />
        </Box>

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <ShareIcon />}
          onClick={handleSubmit}
          disabled={loading}
          sx={{ alignSelf: 'flex-start', bgcolor: dark.purple, fontWeight: 700, textTransform: 'none',
            '&:hover': { bgcolor: '#5a3a7e' }, '&:disabled': { opacity: 0.5 } }}
        >
          {loading ? 'Publishing…' : 'Publish Strategy'}
        </Button>
      </Stack>
    </Box>
  );
}

// ── Upload Trades Tab ─────────────────────────────────────────────────────────
function UploadTradesTab({ strategies }) {
  const [strategyId, setStrategyId] = useState('');
  const [timeframe, setTimeframe] = useState('1m');
  const [asset, setAsset] = useState('NQ');
  const [tradingHour, setTradingHour] = useState('ETH');
  const [signalMappings, setSignalMappings] = useState(
    '{\n  "MW Long": "SMART_BAND_15",\n  "MW Short": "SMART_BAND_15_SHORT"\n}'
  );
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef();

  // Auto-fill when strategy selected
  useEffect(() => {
    if (!strategyId) return;
    const s = strategies.find(s => String(s.id) === String(strategyId));
    if (!s) return;
    setTimeframe(s.timeframe || '1m');
    setAsset(s.asset || 'NQ');
    setTradingHour(s.trading_hour || 'ETH');
    if (s.signal_mappings && Object.keys(s.signal_mappings).length > 0) {
      setSignalMappings(JSON.stringify(s.signal_mappings, null, 2));
    }
  }, [strategyId, strategies]);

  const handleUpload = async () => {
    setError(''); setResult(null);
    if (!file) { setError('Please select a CSV file'); return; }

    const fd = new FormData();
    fd.append('file', file);
    if (strategyId) fd.append('strategy_id', strategyId);
    fd.append('timeframe', timeframe);
    fd.append('asset', asset);
    fd.append('trading_hour', tradingHour);
    if (!strategyId) fd.append('signal_mappings', signalMappings);

    setLoading(true);
    try {
      const resp = await fetch(UPLOAD_API, { method: 'POST', body: fd });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Upload failed');
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: dark.card, color: dark.text,
      '& fieldset': { borderColor: dark.border },
      '&:hover fieldset': { borderColor: dark.accent },
      '&.Mui-focused fieldset': { borderColor: dark.accent },
    },
    '& .MuiInputLabel-root': { color: dark.muted },
    '& .MuiInputLabel-root.Mui-focused': { color: dark.accent },
  };

  const selectSx = {
    bgcolor: dark.card, color: dark.text,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: dark.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dark.accent },
    '& .MuiSvgIcon-root': { color: dark.muted },
  };

  return (
    <Box maxWidth={800}>
      <Typography variant="h6" color={dark.text} fontWeight={700} mb={0.5}>
        Upload TradingView Trades
      </Typography>
      <Typography variant="body2" color={dark.muted} mb={3}>
        Download the <strong style={{ color: dark.accent }}>List of Trades</strong> CSV from TradingView's Strategy
        Report, then publish the signals to TradeWave.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack spacing={2.5}>
        {/* Strategy picker */}
        <FormControl fullWidth size="small">
          <InputLabel sx={{ color: dark.muted }}>Link to a Strategy (optional)</InputLabel>
          <Select value={strategyId} onChange={e => setStrategyId(e.target.value)}
            label="Link to a Strategy (optional)" sx={selectSx}
            MenuProps={{ PaperProps: { sx: { bgcolor: dark.card, color: dark.text } } }}>
            <MenuItem value=""><em style={{ color: dark.muted }}>— Manual configuration —</em></MenuItem>
            {strategies.map(s => (
              <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Metadata row */}
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: dark.muted }}>Timeframe</InputLabel>
              <Select value={timeframe} onChange={e => setTimeframe(e.target.value)} label="Timeframe" sx={selectSx}
                MenuProps={{ PaperProps: { sx: { bgcolor: dark.card, color: dark.text } } }}>
                {TIMEFRAMES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: dark.muted }}>Asset</InputLabel>
              <Select value={asset} onChange={e => setAsset(e.target.value)} label="Asset" sx={selectSx}
                MenuProps={{ PaperProps: { sx: { bgcolor: dark.card, color: dark.text } } }}>
                {ASSETS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: dark.muted }}>Trading Hours</InputLabel>
              <Select value={tradingHour} onChange={e => setTradingHour(e.target.value)} label="Trading Hours" sx={selectSx}
                MenuProps={{ PaperProps: { sx: { bgcolor: dark.card, color: dark.text } } }}>
                {TRADING_HOURS.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Signal mappings (only shown when no strategy linked) */}
        {!strategyId && (
          <Box>
            <Typography variant="caption" color={dark.muted} display="block" mb={0.5}>
              Signal Mappings (JSON) — TradingView signal name → TradeWave signal type
            </Typography>
            <TextField fullWidth multiline rows={4} value={signalMappings}
              onChange={e => setSignalMappings(e.target.value)}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              sx={fieldSx} />
          </Box>
        )}

        {/* File picker */}
        <Box>
          <Typography variant="caption" color={dark.muted} display="block" mb={1}>
            TradingView Strategy Report CSV (List of Trades)
          </Typography>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }}
            onChange={e => setFile(e.target.files[0] || null)} />
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="outlined" startIcon={<UploadIcon />}
              onClick={() => fileRef.current?.click()}
              sx={{ textTransform: 'none', borderColor: dark.border, color: dark.muted,
                '&:hover': { borderColor: dark.accent, color: dark.accent } }}>
              {file ? 'Change file' : 'Select CSV file'}
            </Button>
            {file && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color={dark.text}>{file.name}</Typography>
                <Chip label={`${(file.size / 1024).toFixed(1)} KB`} size="small"
                  sx={{ bgcolor: alpha(dark.accent, 0.1), color: dark.accent, fontSize: '0.65rem' }} />
                <IconButton size="small" onClick={() => { setFile(null); fileRef.current.value = ''; }}
                  sx={{ color: dark.muted }}>
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Stack>
        </Box>

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleUpload}
          disabled={loading || !file}
          sx={{ alignSelf: 'flex-start', bgcolor: dark.green, color: '#000', fontWeight: 700,
            textTransform: 'none', '&:hover': { bgcolor: '#00cc6e' }, '&:disabled': { opacity: 0.5 } }}
        >
          {loading ? 'Publishing…' : 'Publish to TradeWave'}
        </Button>

        {/* Result */}
        {result && (
          <Paper sx={{ p: 2.5, bgcolor: dark.card, border: `1px solid ${alpha(dark.green, 0.3)}` }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <CheckCircleIcon sx={{ color: dark.green }} />
              <Typography fontWeight={700} color={dark.green}>Published Successfully</Typography>
            </Stack>

            <Grid container spacing={2} mb={2}>
              {[
                { label: 'Signals Published', value: result.signals_processed, color: dark.green },
                { label: 'Rows Skipped', value: result.skipped, color: dark.amber },
                { label: 'Webhook Status', value: result.webhook_status, color: result.webhook_status === 200 ? dark.green : '#ef4444' },
              ].map(({ label, value, color }) => (
                <Grid item xs={4} key={label}>
                  <Box sx={{ bgcolor: '#0a0e14', borderRadius: 1, p: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
                    <Typography variant="caption" color={dark.muted}>{label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {result.payload_preview && result.payload_preview.length > 0 && (
              <>
                <Typography variant="caption" color={dark.muted} display="block" mb={1}>
                  Sample signals (first {result.payload_preview.length} of {result.signals_processed})
                </Typography>
                <TableContainer sx={{ bgcolor: '#0d1117', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Signal', 'Asset', 'Timeframe', 'Trading Hours', 'Price', 'Timestamp'].map(h => (
                          <TableCell key={h} sx={{ color: dark.muted, fontSize: '0.7rem', borderColor: dark.border, fontWeight: 600 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.payload_preview.map((sig, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ color: '#c084fc', fontSize: '0.72rem', borderColor: dark.border, fontFamily: 'monospace' }}>{sig.signal}</TableCell>
                          <TableCell sx={{ color: dark.text, fontSize: '0.72rem', borderColor: dark.border }}>{sig.asset}</TableCell>
                          <TableCell sx={{ color: dark.accent, fontSize: '0.72rem', borderColor: dark.border }}>{sig.timeframe}</TableCell>
                          <TableCell sx={{ color: dark.amber, fontSize: '0.72rem', borderColor: dark.border }}>{sig.trading_hour}</TableCell>
                          <TableCell sx={{ color: dark.green, fontSize: '0.72rem', borderColor: dark.border }}>{sig.price.toLocaleString()}</TableCell>
                          <TableCell sx={{ color: dark.muted, fontSize: '0.7rem', borderColor: dark.border, fontFamily: 'monospace' }}>{sig.signal_timestamp}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        )}
      </Stack>
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PineScriptHub() {
  const [tab, setTab] = useState(0);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const fetchStrategies = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const resp = await fetch(HUB_API);
      if (!resp.ok) throw new Error('Failed to load strategies');
      const data = await resp.json();
      setStrategies(data.strategies || []);
    } catch (e) {
      setFetchError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStrategies(); }, [fetchStrategies]);

  const tabSx = {
    color: dark.muted, textTransform: 'none', fontWeight: 600, minWidth: 140,
    '&.Mui-selected': { color: dark.accent },
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: dark.bg, p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: alpha(dark.accent, 0.12), display: 'flex' }}>
          <CodeIcon sx={{ color: dark.accent, fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={800} color={dark.text}>PineScript Hub</Typography>
          <Typography variant="body2" color={dark.muted}>
            Share strategies · Browse PineScript · Publish TradingView trade data
          </Typography>
        </Box>
        <Chip label={`${strategies.length} strategies`} size="small"
          sx={{ ml: 'auto', bgcolor: alpha(dark.accent, 0.1), color: dark.accent, fontWeight: 700 }} />
      </Stack>

      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button size="small" onClick={fetchStrategies} sx={{ color: 'inherit' }}>Retry</Button>
        }>{fetchError}</Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ bgcolor: dark.card, border: `1px solid ${dark.border}`, borderRadius: 2, p: 2.5 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: `1px solid ${dark.border}`, mb: 0,
            '& .MuiTabs-indicator': { bgcolor: dark.accent } }}>
          <Tab label="Browse Strategies" icon={<VisibilityIcon fontSize="small" />} iconPosition="start" sx={tabSx} />
          <Tab label="Share a Strategy" icon={<ShareIcon fontSize="small" />} iconPosition="start" sx={tabSx} />
          <Tab label="Upload Trades" icon={<CloudUploadIcon fontSize="small" />} iconPosition="start" sx={tabSx} />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <BrowseTab strategies={strategies} loading={loading} />
        </TabPanel>
        <TabPanel value={tab} index={1}>
          <ShareTab onPublished={fetchStrategies} />
        </TabPanel>
        <TabPanel value={tab} index={2}>
          <UploadTradesTab strategies={strategies} />
        </TabPanel>
      </Paper>
    </Box>
  );
}
